import { AccessLevel } from "@/api/williams/users";
import { ConfirmationDialog } from "@/components/dialogs";
import { faCheck } from "@fortawesome/free-solid-svg-icons";
import { faFilePdf, faSave } from "@fortawesome/free-regular-svg-icons";
import { FormProvider, useForm } from "react-hook-form";
import { IconButton } from "../../components";
import { SavePrompt } from "@/components";
import { TestEventResults } from "@/api/williams/testEvents";
import { TestEventResultsSchema } from "./TestResultsSchema";
import { TestResultsBreadcrumbs } from "./TestResultsBreadcrumbs";
import { useLoadingScreen, useModalDialog } from "@/hooks";
import { usePermissions } from "../../users/hooks";
import { usePrintTestEvent, useSaveTestResults, useSubmitTestResults } from "../hooks";
import { WelderSection } from "./TestResultsWelder";
import { zodResolver } from "@hookform/resolvers/zod";

export function TestResultsForm({ results }: { results: TestEventResults }) {
  const form = useForm<TestEventResults>({
    defaultValues: results,
    resolver: zodResolver(TestEventResultsSchema),
    reValidateMode: "onChange",
  });

  const data = form.watch();
  const save = useSaveTestResults();
  const submit = useSubmitTestResults();
  const print = usePrintTestEvent();
  const dialog = useModalDialog();
  const permissions = usePermissions();
  const loader = useLoadingScreen();

  const isDirty = Object.keys(form.formState.dirtyFields).length > 0;
  const isLocked = data.testEvent.locked || permissions.data?.accessLevel === AccessLevel.GeneralUser;

  function handleSave(data: TestEventResults) {
    save.mutate(data, {
      onSuccess: () => form.reset(data),
    });
  }

  function handleSubmit(data: TestEventResults) {
    dialog.show(ConfirmationDialog, {
      caption: "Submit Results",
      message: "This will create certificates and update qualifications. Are you sure?",
      onAccept: () => {
        loader.show();
        submit.mutate(data, {
          onSuccess: (updatedData) => form.reset(updatedData),
          onSettled: () => loader.hide(),
        });
      },
    });
  }

  return (
    <SavePrompt when={isDirty}>
      <div style={{ maxWidth: 1000 }}>
        <FormProvider {...form}>
          <TestResultsBreadcrumbs testEvent={results.testEvent} />

          <div className="d-md-flex justify-content-between mb-2">
            <h3>Test Results</h3>
            <div className="d-flex gap-2 my-auto">
              <IconButton
                icon={faSave}
                title="Save"
                onClick={() => handleSave(data)}
                disabled={!isDirty || isLocked}
                isLoading={save.isLoading}
              />
              <IconButton
                icon={faCheck}
                title="Submit Results"
                onClick={form.handleSubmit(handleSubmit)}
                disabled={data.welders.every((x) => x.templates.every((x) => x.submitted)) || isLocked}
                isLoading={submit.isLoading}
              />
              <IconButton icon={faFilePdf} title="Download PDF" onClick={() => print.showDialog(results.testEvent)} />
            </div>
          </div>

          {data.welders.map((welder, index) => (
            <WelderSection key={welder.welderId} welder={welder} path={`welders.${index}`} isLocked={isLocked} />
          ))}
        </FormProvider>
      </div>
    </SavePrompt>
  );
}
